import type { CodeWorkspaceDto, ConversationDto, ConversationFileDto, ErrorCode, Microsoft365Source, WorkspaceDto } from '@dbrain/shared';
import { Icons, Select, type IconComponent } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, isApiError, upload } from '../api/client.js';
import { keys, useCompanyZone, useDashboard, useMe, useModels } from '../api/hooks.js';
import { useReadOnly } from '../api/license.js';
import { useMicrosoft365Status } from '../api/microsoft365.js';
import { addDraftFile } from '../api/microsoft365-files.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { ReadOnlyCard } from '../components/license/ReadOnlyCard.js';
import { AddMenu, AddMenuRow } from '../components/microsoft365/files/AddMenu.js';
import { FilePicker } from '../components/microsoft365/files/FilePicker.js';
import { ImportNotice, type ImportSummary } from '../components/microsoft365/files/ImportResult.js';
import { addMenuView, PICKER_PARAM, pickerSourceOf, type PickedFile } from '../components/microsoft365/files/model.js';
import { ChatModelMenu } from '../components/chat/ChatModelMenu.js';
import { NewWorkspaceButton } from '../components/workspaces/NewWorkspace.js';
import { OversizeChip, OversizeNotice } from '../components/plan/PlanParts.js';
import { usePlan, useUploadLimitMb } from '../api/plan.js';
import { ComposerFiles, useComposerFiles, useDropOnLeave } from '../components/chat/ComposerFiles.js';
import { SlashHelp, SlashMenu } from '../components/chat/SessionParts.js';
import { useSlashMenu } from '../components/chat/useSlashMenu.js';
import { SkillAddDialog } from '../components/skills/SkillAddDialog.js';
import { useExperience, useMeta } from '../experience.js';
import { forgetLandingPrompt, peekLandingPrompt } from './bireysel/landing-prompt.js';
import { homeWorkspace } from '@dbrain/app-core/home-workspace';
import { HOME_COMMANDS, parseSlash, replayEnters, skillAddSource, type SlashCommand } from '@dbrain/app-core/slash';

/**
 * The home page of both experiences (docs/design/mockups/Main.dc.html): one
 * question, the rounded box, a few starting points. Sending starts a
 * conversation in the most recently used workspace of this experience. The
 * box has the conversation's "/" menu: the skills of that workspace (so
 * "/e-posta-yazici …" starts a conversation with the skill) and the
 * commands that work before a conversation exists.
 *
 * Just after a restart the page may for a moment not know that workspace or
 * its skills. The menu shows the skills read without a workspace meanwhile;
 * an Enter waits, and then does what it would have done (D-80).
 *
 * In CompanyCode someone without a code workspace just starts: the first
 * message (or file) opens their own (D-152). The send button never looks
 * dead: when something stands in the way, pressing it says what in one line
 * (v0.15a.3).
 */
const SUGGESTIONS: Record<'chat' | 'code', { key: string; icon: IconComponent; tone: string }[]> = {
  chat: [
    { key: 'contract', icon: Icons.documents, tone: 'pink' },
    { key: 'excel', icon: Icons.chart, tone: 'green' },
    { key: 'web', icon: Icons.web, tone: 'blue' },
  ],
  code: [
    { key: 'tests', icon: Icons.plan, tone: 'green' },
    { key: 'game', icon: Icons.monitor, tone: 'blue' },
    { key: 'readme', icon: Icons.fileCode, tone: 'pink' },
  ],
};

const DEFAULT_UPLOAD_MB = 25;

/** v0.15a.3: refusals of a first message that say why in one line under the box, not a whole alert */
const ONE_LINE: readonly ErrorCode[] = ['quota_exceeded', 'no_model_configured', 'model_unavailable', 'model_not_allowed', 'license_read_only', 'product_not_licensed'];

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const experience = useExperience();
  // D-124: past the license's grace the message box gives way to the read-only card
  const readOnly = useReadOnly();
  const meta = useMeta();
  const zone = useCompanyZone();
  const dashboard = useDashboard();
  const models = useModels();
  // Phase 13d: a company account's team workspaces (its directory domains)
  const me = useMe().data;
  const domains = me?.account?.kind === 'directory' ? (me.domains ?? []) : [];
  const [text, setText] = useState('');
  const [tooLarge, setTooLarge] = useState(false);
  const [modelRef, setModelRef] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [help, setHelp] = useState(false);
  const [skillAdd, setSkillAdd] = useState<string | null>(null);
  // what stood in the way of the last press of send: no workspace to start in, no model to answer
  const [blocked, setBlocked] = useState<'workspace' | 'model' | null>(null);
  // a conversation's own command typed here: it needs a conversation first
  const [elsewhere, setElsewhere] = useState<SlashCommand | null>(null);
  // the box at each Enter pressed before the page knew where the conversation goes and its skills there
  const [held, setHeld] = useState<string[]>([]);
  const input = useRef<HTMLTextAreaElement>(null);
  // `/model` opens the picker (16c.1: a menu, B2cModelSecici)
  const [modelOpen, setModelOpen] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  // D-266: the person's plan's file size where plans exist (B2cDosyaBoyutu), otherwise the installation's
  const plan = usePlan();
  const planUploadMb = useUploadLimitMb();
  const maxUploadMb = plan ? planUploadMb : (meta.data?.uploadMaxMb ?? DEFAULT_UPLOAD_MB);
  const [oversize, setOversize] = useState<{ name: string; size: number } | null>(null);
  // 16c.2 (D-374): the example a visitor picked on the landing page waits in the box after their first sign-in, unsent
  const landingEdition = meta.data?.capabilities?.plans === true;
  useEffect(() => {
    if (!landingEdition) return;
    const waiting = peekLandingPrompt();
    if (waiting === null) return;
    forgetLandingPrompt();
    setText((current) => current || waiting);
  }, [landingEdition]);

  // where a new conversation goes (D-219): in the chat the person's own "Kişisel" unless they pick another; in the code product their pick, else the last used
  const { writable, target } = useMemo(
    () =>
      homeWorkspace({
        workspaces: dashboard.data?.workspaces ?? [],
        experience,
        recentWorkspaceId: dashboard.data?.recentConversations.find((c) => c.profile === experience)?.workspaceId,
        chosen,
      }),
    [dashboard.data, experience, chosen],
  );
  // "değiştir" next to where the chat starts shows the list of workspaces
  const [picking, setPicking] = useState(false);
  // D-152: in CompanyCode without a code workspace the first message (or file) opens the user's own
  const personal = experience === 'code' && dashboard.isSuccess && writable.length === 0;
  const opened = useRef(false);
  const opening = useRef<Promise<WorkspaceDto> | null>(null);
  const placeFor = (): Promise<WorkspaceDto> => {
    if (target) return Promise.resolve(target);
    if (!personal) return Promise.reject(new Error('no workspace'));
    opening.current ??= api<CodeWorkspaceDto>('POST', '/api/workspaces/personal-code').then(
      ({ workspace, created }) => {
        if (created) opened.current = true;
        void queryClient.invalidateQueries({ queryKey: keys.dashboard });
        return workspace;
      },
      (error: unknown) => {
        opening.current = null;
        throw error;
      },
    );
    return opening.current;
  };

  const task = experience === 'code' ? 'code' : 'main';
  // 16c.1: only what this person may pick (their package's in bireysel, their teams' in kurumsal)
  const options = (models.data?.models ?? []).filter((m) => m.kind === 'chat' && m.enabled && m.availability.ok && m.allowed);
  const noModel = models.isSuccess && options.length === 0;

  // D-93: an attached file is uploaded and read at once, before the conversation exists, under the id it will get
  const draft = useRef<string | null>(null);
  const sent = useRef(false);
  const dropUpload = (file: ConversationFileDto) => api('DELETE', `/api/uploads/${file.id}`);
  const attachments = useComposerFiles({
    upload: async (file) => {
      const place = await placeFor();
      const dto = await upload<ConversationFileDto>('POST', '/api/uploads', file, {
        'x-file-name': encodeURIComponent(file.name || `pasted-${Date.now()}.png`),
        'x-workspace-id': place.id,
        ...(draft.current ? { 'x-draft-id': draft.current } : {}),
      });
      if (dto.draftId) draft.current = dto.draftId;
      return dto;
    },
    remove: dropUpload,
  });
  // leaving the page without sending takes the uploads back
  useDropOnLeave(() => (sent.current ? [] : attachments.unsent()), dropUpload);
  const addFiles = (picked: File[]) => {
    if (picked.length === 0) return;
    const fits = picked.filter((file) => file.size <= maxUploadMb * 1024 * 1024);
    const over = picked.find((file) => file.size > maxUploadMb * 1024 * 1024);
    // with plans the file over the size stays as a marked chip, with the plan's words under the box
    if (plan) setOversize(over ? { name: over.name, size: over.size } : null);
    else setTooLarge(fits.length < picked.length);
    attachments.add(fits);
  };

  // Microsoft 365 part 3 (MsEkleMenu, MsBelgeSec): the "+" group in Chat and Cowork, the picker on a source; "Bu
  // sohbet" goes into the box like an upload (D-93's draft), a library pick is linked once the conversation exists
  const microsoft365 = useMicrosoft365Status(experience === 'chat');
  const microsoftMenu = addMenuView(microsoft365.data, experience);
  const [picker, setPicker] = useState<{ source: Microsoft365Source; signInError: string | null } | null>(null);
  const [imported, setImported] = useState<ImportSummary | null>(null);
  const linked = useRef<string[]>([]);
  // back from Microsoft's page with the picker's way back: it opens again on the same source
  const [params, setParams] = useSearchParams();
  const pickerBack = pickerSourceOf(params);
  useEffect(() => {
    if (!pickerBack) return;
    setPicker({ source: pickerBack, signInError: params.get('microsoft365') === 'error' ? params.get('reason') : null });
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of [PICKER_PARAM, 'microsoft365', 'reason']) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [pickerBack, params, setParams]);
  const addFromMicrosoft = (files: PickedFile[]) =>
    attachments.addSource(
      files.map((file) => ({
        name: file.name,
        size: file.size ?? 0,
        fetch: async () => {
          const place = await placeFor();
          const dto = await addDraftFile({ workspaceId: place.id, ...(draft.current ? { draftId: draft.current } : {}), ...file.item });
          if (dto.draftId) draft.current = dto.draftId;
          return dto;
        },
      })),
    );

  const start = useMutation({
    mutationFn: async (message: string) => {
      const place = await placeFor();
      // a file still uploading goes once it is up; one still being read is waited for by the turn (D-92)
      await attachments.settled();
      const attachmentIds = attachments.ids();
      const conversation = await api<ConversationDto>('POST', `/api/workspaces/${place.id}/conversations`, attachmentIds.length > 0 && draft.current ? { draftId: draft.current } : {});
      if (modelRef) await api('PATCH', `/api/conversations/${conversation.id}`, { modelRef });
      // documents brought from Microsoft 365 into the library from this box: this conversation searches them too
      await Promise.all(linked.current.map((documentId) => api('POST', `/api/conversations/${conversation.id}/documents`, { documentId }).catch(() => undefined)));
      await api('POST', `/api/conversations/${conversation.id}/messages`, { text: message, ...(attachmentIds.length > 0 ? { attachmentIds } : {}) });
      return conversation;
    },
    onSuccess: (conversation) => {
      // the conversation took the uploads over
      sent.current = true;
      draft.current = null;
      linked.current = [];
      attachments.clear();
      void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
      void queryClient.invalidateQueries({ queryKey: keys.dashboard });
      // D-152: the conversation says once that the user's own code workspace was opened for it
      void navigate(`/c/${conversation.id}`, opened.current ? { state: { personalWorkspace: true } } : undefined);
    },
  });
  // the same "/" menu as in a conversation (QW-113, QW-1205); the skills are read at once, without
  // a workspace, and those of the workspace replace them once it is known (D-80)
  const slash = useSlashMenu({ text, setText, place: target ? { workspaceId: target.id } : {}, enabled: true, keepPrevious: true, commands: HOME_COMMANDS });
  // where a new conversation goes is known, and so are the skills it has there
  const ready = dashboard.isSuccess ? !target || slash.settled : dashboard.isError;
  const runCommand = (command: SlashCommand, argument: string) => {
    switch (command) {
      case 'help':
        setHelp(true);
        break;
      case 'model':
        setModelOpen((n) => n + 1);
        break;
      case 'memory':
        void navigate('/memory');
        break;
      case 'skill': {
        const source = skillAddSource(argument);
        if (source !== null && target) setSkillAdd(source);
        else void navigate('/skills');
        break;
      }
      default:
        break;
    }
  };
  const submit = (value: string) => {
    const message = value.trim();
    if (!message || start.isPending) return;
    // a skill (`/name …`) is not a command: it goes to the new conversation, which runs it
    const command = parseSlash(message);
    if (command) {
      if (HOME_COMMANDS.includes(command.command)) {
        runCommand(command.command, command.argument);
        setText('');
      } else setElsewhere(command.command);
      return;
    }
    // v0.15a.3: what stands in the way is said in one line, the button never just stays grey
    const stop = !target && !personal ? 'workspace' : noModel ? 'model' : null;
    setBlocked(stop);
    if (stop) {
      start.reset();
      return;
    }
    start.mutate(message);
  };
  // an Enter before the page is ready waits: sent now, a skill could go as plain text or nowhere (D-80)
  const enter = () => {
    if (!text.trim()) return;
    if (ready && held.length === 0) submit(text);
    else setHeld((current) => [...current, text]);
  };
  // once it is, the Enters go in order as they would have, on the skills of that workspace
  const replay = useRef(() => {});
  useLayoutEffect(() => {
    replay.current = () => {
      // without the skills there is no telling what an Enter would have done: it is set aside, the text stays
      const { text: next, send } = slash.failed ? { text, send: false } : replayEnters(text, held, slash.complete);
      setHeld([]);
      setText(next);
      if (send) submit(next);
    };
  });
  useEffect(() => {
    if (ready && held.length > 0) replay.current();
  }, [ready, held]);
  const busy = start.isPending || held.length > 0;
  const waiting = dashboard.data?.pendingApprovals ?? [];
  // a refusal of the first message in one line (the budget says when it resets); anything else as an alert
  const refusal = isApiError(start.error) && ONE_LINE.includes(start.error.code) ? start.error : null;
  const quota = refusal?.code === 'quota_exceeded' ? refusal.quota : undefined;
  // v0.15a.3: where previews cannot open, the game is one file to download instead of a page in the preview
  const suggestions = SUGGESTIONS[experience].map((s) => (s.key === 'game' && meta.data?.previews === false ? { ...s, key: 'gameFiles' } : s));

  return (
    <div className="home">
      <h1 className="home__title" data-testid="dashboard-greeting">
        {t(experience === 'code' ? 'home.titleCode' : 'home.title')}
      </h1>
      {readOnly ? (
        <ReadOnlyCard />
      ) : (
        <form
          className="composer-box composer-box--home"
          onSubmit={(e) => {
            e.preventDefault();
            enter();
          }}
          data-testid="home-composer"
        >
          <AddMenu
            label={t('home.add')}
            experience={experience}
            maxUploadMb={maxUploadMb}
            onUpload={() => fileInput.current?.click()}
            uploadDisabled={!target && !personal}
            microsoft={microsoftMenu}
            onMicrosoft={(source) => setPicker({ source, signInError: null })}
            testId="home-add"
            uploadTestId="home-attach"
          >
            <AddMenuRow icon={<Icons.skills size={17} />} title={t('home.useSkill')} hint={t('microsoft365.files.menu.skillHome')} to="/skills" />
            <AddMenuRow icon={<Icons.connections size={17} />} title={t('home.addConnection')} hint={t('microsoft365.files.menu.connection')} to="/connectors" />
          </AddMenu>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="ui-visually-hidden"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const picked = [...(e.target.files ?? [])];
              e.target.value = '';
              addFiles(picked);
              input.current?.focus();
            }}
            data-testid="home-file"
          />
          <label htmlFor="home-input" className="ui-visually-hidden">
            {t('home.inputLabel')}
          </label>
          <div className="composer-box__main">
          {oversize ? (
            <ul className="chips composer-files">
              <OversizeChip name={oversize.name} size={oversize.size} onRemove={() => setOversize(null)} />
            </ul>
          ) : null}
          <ComposerFiles entries={attachments.entries} onRemove={attachments.remove} />
          <textarea
            id="home-input"
            ref={input}
            className="composer-box__input"
            rows={1}
            placeholder={t(experience === 'code' ? 'home.placeholderCode' : 'home.placeholder')}
            value={text}
            onChange={(e) => {
              const value = e.target.value;
              // typing on keeps the Enters that wait; deleting or changing what was there sets them aside
              if (held.length > 0 && !value.startsWith(text)) setHeld([]);
              setText(value);
              setElsewhere(null);
              setBlocked(null);
            }}
            onPaste={(e) => {
              const pasted = [...e.clipboardData.files];
              if (pasted.length > 0 && (target || personal)) {
                e.preventDefault();
                addFiles(pasted);
              }
            }}
            onKeyDown={(e) => {
              // until the page is ready the menu only shows: its keys would act on skills that may still change
              if (ready && held.length === 0 && slash.onKeyDown(e)) return;
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                enter();
              }
            }}
            data-testid="home-input"
          />
          </div>
          {/* 16c.1 (B2cModelSecici): the person's models, Pro's with a lock on Ücretsiz */}
          <ChatModelMenu key={modelOpen} value={modelRef || null} onPick={(ref) => setModelRef(ref ?? '')} task={task} variant="home" open={modelOpen > 0} data-testid="home-model" />
          <button
            type="submit"
            className="ui-icon-button ui-icon-button--primary ui-icon-button--lg"
            aria-label={t('conversation.send')}
            title={t('conversation.send')}
            aria-busy={busy}
            disabled={!text.trim() || busy}
            data-testid="home-send"
          >
            {busy ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : <Icons.send size={20} />}
          </button>
          {/* while an Enter waits the box is about to change: no menu for the text as it is */}
          <SlashMenu entries={held.length > 0 ? [] : slash.entries} active={slash.active} onPick={slash.pick} />
        </form>
      )}

      <div className="home__context">
        {/* B2cDosyaBoyutu: the plan's file size under the box */}
        {plan ? (
          <p className="composer__hint home__file-hint" data-testid="home-file-limit">
            {t('conversation.attachmentLimit', { mb: maxUploadMb })}
          </p>
        ) : null}
        {tooLarge ? <p className="field-error">{t('home.tooLarge', { mb: maxUploadMb })}</p> : null}
        {oversize ? <OversizeNotice size={oversize.size} limitMb={maxUploadMb} /> : null}
        {imported ? <ImportNotice summary={imported} linked="home" onClose={() => setImported(null)} /> : null}
        {elsewhere ? (
          <p className="home__note" role="status" data-testid="home-slash-note">
            {t('slash.onlyInConversation', { command: `/${elsewhere}` })}
          </p>
        ) : null}
        {help ? <SlashHelp commands={HOME_COMMANDS} onClose={() => setHelp(false)} /> : null}
        {blocked === 'model' ? (
          <p className="home__blocked" role="alert" data-testid="home-blocked">
            {t('home.noModel')}
          </p>
        ) : null}
        {personal ? (
          <p className="home__note" data-testid="home-personal-hint">
            {t('home.personalCode')} <NewWorkspaceButton kind="code" testId="home-new-workspace" />
          </p>
        ) : dashboard.data && writable.length === 0 ? (
          <p
            className={blocked === 'workspace' ? 'home__note home__note--blocked' : 'home__note'}
            role={blocked === 'workspace' ? 'alert' : undefined}
            data-testid={blocked === 'workspace' ? 'home-blocked' : undefined}
          >
            {t('home.noWorkspace')} <NewWorkspaceButton kind={experience} testId="home-new-workspace" />
          </p>
        ) : target && writable.length > 1 && (experience === 'code' || picking) ? (
          <label className="home__workspace">
            <Icons.workspace size={16} />
            <span>{t('home.in')}</span>
            <Select
              size="sm"
              value={target.id}
              autoFocus={picking}
              onChange={(e) => {
                setChosen(e.target.value);
                setPicking(false);
              }}
              onBlur={() => setPicking(false)}
              data-testid="home-workspace"
            >
              {writable.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.personal ? t('home.personalName') : w.name}
                </option>
              ))}
            </Select>
          </label>
        ) : target ? (
          <p className="home__workspace" data-testid="home-workspace-line">
            <Icons.workspace size={16} />
            <span>{target.personal ? t('home.inPersonal') : t('home.inWorkspace', { name: target.name })}</span>
            {writable.length > 1 ? (
              <>
                <span aria-hidden="true">·</span>
                <button type="button" className="home__change" onClick={() => setPicking(true)} data-testid="home-workspace-change">
                  {t('home.change')}
                </button>
              </>
            ) : null}
          </p>
        ) : null}
        {refusal ? (
          <p className="home__blocked" role="alert" data-testid="home-blocked" data-code={refusal.code}>
            {quota ? (
              <>
                {t(`quotaNotice.${quota.scope}`)}{' '}
                <Trans i18nKey="quotaNotice.resets" values={{ when: new Date(quota.resetsAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone }) }} components={{ b: <strong /> }} />
              </>
            ) : (
              t(`errors.${refusal.code}.title`)
            )}
          </p>
        ) : null}
        <ErrorAlert error={refusal ? attachments.error : (start.error ?? attachments.error)} />
      </div>

      {domains.length > 0 ? (
        <nav className="home__domains" aria-label={t('home.domains')} data-testid="home-domains">
          <span>{t('home.domains')}</span>
          {domains.map((domain) => (
            <Link key={domain.id} className="home__domain" to={`/w/${domain.workspaceId}`} data-testid={`home-domain-${domain.name}`}>
              <Icons.workspace size={16} />
              {domain.name}
            </Link>
          ))}
        </nav>
      ) : null}

      {waiting.length > 0 ? (
        <Link className="home__waiting" to={`/c/${waiting[0]?.conversationId}`} data-testid="home-waiting">
          <Icons.warning size={18} />
          {t('home.waiting', { count: waiting.length })}
          <Icons.chevronRight size={16} />
        </Link>
      ) : null}

      <ul className="suggestions" aria-label={t('home.suggestionsLabel')}>
        {suggestions.map(({ key, icon: Icon, tone }) => (
          <li key={key}>
            <button
              type="button"
              className="suggestion"
              onClick={() => {
                setText(t(`home.suggestions.${experience}.${key}.text`));
                input.current?.focus();
              }}
              data-testid={`suggestion-${key}`}
            >
              <span className={`suggestion__icon suggestion__icon--${tone}`} aria-hidden="true">
                <Icon size={18} />
              </span>
              <span className="suggestion__text">
                <Trans i18nKey={`home.suggestions.${experience}.${key}.label`} components={{ b: <strong /> }} />
              </span>
            </button>
          </li>
        ))}
      </ul>

      <Link to="/skills" className="home__discover">
        {t('home.discover')}
        <Icons.chevronRight size={18} />
      </Link>
      {skillAdd !== null && target ? <SkillAddDialog initialSource={skillAdd} workspaceId={target.id} onClose={() => setSkillAdd(null)} /> : null}
      {picker ? (
        <FilePicker
          source={picker.source}
          composer={Boolean(target || personal)}
          returnPath="/"
          signInError={picker.signInError}
          onClose={() => {
            setPicker(null);
            input.current?.focus();
          }}
          onAddToConversation={addFromMicrosoft}
          onImported={(summary) => {
            setImported(summary);
            linked.current = [...new Set([...linked.current, ...summary.result.added.map((item) => item.document.id)])];
          }}
        />
      ) : null}
    </div>
  );
}
